// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getStorage } from '../utils/functions/localstorage';
import { checkAccessRights } from '../utils/functions/accessRight';
import { ROLES } from '../utils/constants';
// Define a service using a base URL and expected endpoints

const USER = process.env.REACT_APP_API_URL;
const ADMIN = process.env.REACT_APP_API_URL_admin;

// 1. สร้าง Base Query พื้นฐานเพื่อแนบ Token
const rawBaseQuery = fetchBaseQuery({
  prepareHeaders: (headers) => {
    const token = getStorage('token');

    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

// 2. สร้าง Dynamic Query เพื่อสลับราง URL
const dynamicBaseQuery = async (args, api, extraOptions) => {
  const isAdmin = checkAccessRights(ROLES.ADMIN_ROLE) || checkAccessRights(ROLES.SUPER_ADMIN_ROLE);
  const isSale = checkAccessRights(ROLES.SALE_ROLE);

  let targetUrl = USER; // ค่าเริ่มต้นวิ่งไป USER

  if (isAdmin) {
    targetUrl = ADMIN; // ถ้าเป็น Admin วิ่งไป ADMIN เสมอ
  } else if (isSale) {
    // ✅ นำชื่อ Endpoint ที่ต้องการให้ SALE ยิงไปเส้น ADMIN มารวมไว้ตรงนี้
    const adminEndpointsForSale = [
      'waitingForPayment', // เส้น GET ตาราง
      'historyForPayment',
      'transactionForPayment',
      'transactionOfflineForPayment',
      'deleteWaiting' // ✅ เพิ่มเส้น DELETE ให้วิ่งไปหา Admin ด้วย
    ];

    if (adminEndpointsForSale.includes(api.endpoint)) {
      targetUrl = ADMIN; // ให้ใช้เส้น ADMIN
    } else {
      targetUrl = USER;  // นอกนั้น (เช่น POST ต่างๆ) ให้ใช้เส้น USER
    }
  }

  // นำ URL ปลายทางมาประกอบร่างกับ Path
  const urlPath = typeof args === 'string' ? args : (args.url || '');
  const customArgs = typeof args === 'string'
    ? `${targetUrl}/payment-transaction${urlPath}`
    : { ...args, url: `${targetUrl}/payment-transaction${urlPath}` };

  // ส่ง Request ออกไป
  return rawBaseQuery(customArgs, api, extraOptions);
};

export const paymentTransactionApi = createApi({
  reducerPath: 'paymentTransactionApi',
  baseQuery: dynamicBaseQuery, // ✅ เรียกใช้ Dynamic Query
  endpoints: (builder) => ({
    waitingForPayment: builder.query({
      query: (q) =>
        `?sorgcode=${q.sorgcode}&customerCode=${q.customerCode}&page=${q.page}&size=${q.size}&hashTaxId=${q.hashTaxId}&federalTaxId=${q.federalTaxId}&search=${q.search}&sortBy=${q.sortBy || 'createdAt'}&sortOrder=${q.sortOrder || 'desc'}`,
    }),
    deleteWaiting: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        const { id } = params;
        return {
          url: `/${id}?sorgcode=${body.sorgcode}&customerCode=${body.customerCode}&federalTaxId=${body.federalTaxId}`,
          method: 'DELETE',
        };
      },
    }),
    uploadTransSlip: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        // console.log('params.sorgcode', params.sorgcode);
        // console.log('params.customerCode', params.customerCode);
        return {
          url: `/upload/orderid?sorgcode=${params.sorgcode}&customerCode=${params.customerCode}`,
          body,
          method: 'POST',
          // headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
    }),
    uploadInvSlip: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        // console.log('params.sorgcode', params.sorgcode);
        // console.log('params.customerCode', params.customerCode);
        return {
          url: `/upload/invoiceno?customerCode=${params.customerCode}&sorgcode=${params.sorgcode}&hashTaxId=${params.hashTaxId}`,
          body,
          method: 'POST',
          // headers: { 'Content-Type': 'multipart/form-data; boundary=<calculated when request is sent>' },
        };
      },
    }),
    historyForPayment: builder.query({
      query: (q) =>
        `/advance-search/?sorgcode=${q.sorgcode}&page=${q.page}&size=${q.size}&customerCode=${q.customerCode}&paymentStatus=${q.paymentStatus}&invoiceNo=${q.invoiceNo}&startDate=${q.startDate}&endDate=${q.endDate}&service=${q.service}&transactionRef=${q.tran_ref}&minPayAmount=${q.minPayAmount}&maxPayAmount=${q.maxPayAmount}&paymentMethod=${q.paymentMethod}&hashTaxId=${q.hashTaxId}&federalTaxId=${q.federalTaxId}&sortBy=${q.sortBy || 'createdAt'}&sortOrder=${q.sortOrder || 'desc'}`,
    }),
    discountPrepaid: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        return {
          url: '/calculate-total',
          body,
          method: 'POST',
          // headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
    }),
    toPayCreate: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        return {
          url: '/create/topay',
          body,
          method: 'POST',
          // headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
    }),
    payCreate: builder.mutation({
      query: (req) => {
        const { params, body } = req;
        return {
          // url: '',
          body,
          method: 'POST',
          // headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
    }),
    transactionForPayment: builder.query({
      query: (q) => {
        const params = new URLSearchParams({
          page: q.page,
          size: q.size,
          sorgcode: q.sorgcode,
          sortBy: q.sortBy || 'transaction_date',
          sortOrder: q.sortOrder || 'desc',
          search: q.search || '',
        });

        return `/bills?${params.toString()}`;
      },
    }),
    transactionOfflineForPayment: builder.query({
      query: (q) => `/offline?invoiceNo=${q.invoiceNo}&sorgcode=${q.sorgcode}`,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
// eslint-disable-next-line max-len
export const {
  useWaitingForPaymentQuery,
  useDeleteWaitingMutation,
  useUploadTransSlipMutation,
  useUploadInvSlipMutation,
  useHistoryForPaymentQuery,
  useDiscountPrepaidMutation,
  useToPayCreateMutation,
  usePayCreateMutation,
  useTransactionForPaymentQuery,
  useLazyTransactionOfflineForPaymentQuery,
} = paymentTransactionApi;

// Download outside the Redux query cache: binary workbooks are not UI state.
export async function exportBillPayments(params) {
  const query = new URLSearchParams(params);
  const response = await fetch(`${ADMIN}/payment-transaction/bills/export?${query.toString()}`, {
    headers: { authorization: `Bearer ${getStorage('token')}` },
  });
  if (!response.ok) throw new Error('Bill payment export failed');
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
    throw new Error('Invalid export response');
  }
  return response.blob();
}
